/**
 * Full invoice history dialog — scrollable list of all invoice groups with
 * expandable line items and a "View Invoice" button to open the Stripe-hosted page.
 */
import {
  CheckCircle,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react'
import { useState } from 'react'

import { APPOINTMENT_TYPES_BY_ID, formatClinicPrice, formatName } from '@snootsvet/shared'

import { FormattedDate } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import type { InvoiceGroup } from '@/frontend/features/billing/invoices.utils'
import { useOpenStripeInvoice } from '@/frontend/features/members/hooks/use-open-stripe-invoice'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

interface MemberInvoicesDialogProps {
  invoices: InvoiceGroup[]
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Scrollable dialog listing all invoice groups with expandable line items. */
export function MemberInvoicesDialog({ invoices, open, onOpenChange }: MemberInvoicesDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] sm:max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Invoice History</DialogTitle>
        </DialogHeader>

        {invoices.length === 0 ? (
          <p className="py-8 text-center text-muted-foreground">No invoices yet</p>
        ) : (
          <div className="space-y-2">
            {invoices.map((group) => (
              <InvoiceGroupCard key={group.paymentId} group={group} />
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Internal sub-components
// ---------------------------------------------------------------------------

/** Derives the payment status for an invoice group from its orders. */
function groupPaymentStatus(group: InvoiceGroup): 'paid' | 'failed' {
  return group.orders.some((o) => o.paymentStatus === 'failed') ? 'failed' : 'paid'
}

/**
 * Derives a consultation subtitle when all orders in the group share the same
 * consultation. Returns null if orders span multiple or no consultations.
 */
function groupConsultationLabel(group: InvoiceGroup): string | null {
  const consultationIds = [...new Set(group.orders.map((o) => o.consultation?.id).filter(Boolean))]
  if (consultationIds.length !== 1) return null

  const consultation = group.orders.find((o) => o.consultation)?.consultation
  if (!consultation) return null

  const parts: string[] = []
  if (consultation.appointment?.appointmentTypeId) {
    const typeName = APPOINTMENT_TYPES_BY_ID[consultation.appointment.appointmentTypeId]?.name
    if (typeName) parts.push(typeName)
  }
  if (consultation.staff) parts.push(`with ${formatName(consultation.staff)}`)

  return parts.length > 0 ? parts.join(' ') : null
}

/** Expandable card for a single invoice group — shows summary and line items. */
function InvoiceGroupCard({ group }: { group: InvoiceGroup }) {
  const [expanded, setExpanded] = useState(false)
  // An invoice is billed in a single currency (enforced server-side), so the
  // whole group takes its currency from its orders' clinic, not the viewer.
  const invoiceClinicId = group.orders[0].clinicId
  const { mutate: openInvoice, isPending } = useOpenStripeInvoice()
  const status = groupPaymentStatus(group)
  const allRefills = group.orders.every((o) => o.isRefill)
  const consultationLabel = allRefills ? null : groupConsultationLabel(group)

  return (
    <div className="rounded-lg border">
      {/* Summary row — click to expand */}
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-3 rounded-t-lg bg-muted/50 px-4 py-3 text-left"
      >
        {expanded ? (
          <ChevronDown className="size-4 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
        )}
        <span className="flex-1">
          <FormattedDate value={group.date} clinicId={group.orders[0]?.clinicId} />
          {consultationLabel && (
            <span className="ml-2 text-sm text-muted-foreground">{consultationLabel}</span>
          )}
        </span>
        {allRefills && (
          <Badge size="sm" variant="info">
            <RefreshCw />
            Refill
          </Badge>
        )}
        {status === 'paid' ? (
          <Badge size="sm" variant="success">
            <CheckCircle />
            Paid
          </Badge>
        ) : (
          <Badge size="sm" variant="destructive">
            <XCircle />
            Failed
          </Badge>
        )}
        <span className="tabular-nums text-muted-foreground">
          {group.orders.length} {group.orders.length === 1 ? 'item' : 'items'}
        </span>
        <span className="tabular-nums font-medium">
          {formatClinicPrice(group.total, invoiceClinicId)}
        </span>
      </button>

      {/* Expanded line items + action */}
      {expanded && (
        <div className="border-t px-4 py-3 space-y-3">
          <div className="space-y-1.5">
            {group.orders.map((order) => (
              <div key={order.id} className="flex items-baseline justify-between">
                <div className="flex items-baseline gap-2 min-w-0 flex-1">
                  <span className="text-muted-foreground">•</span>
                  <span>
                    {order.product.name} × {order.quantity} {order.product.unit}
                    <span className="ml-1.5 text-sm text-muted-foreground">
                      ({order.patient.name})
                    </span>
                    {order.isRefill && <span className="ml-1.5 text-sm text-info-fg">Refill</span>}
                  </span>
                </div>
                <span className="tabular-nums shrink-0 ml-3">
                  {formatClinicPrice(order.totalPrice, order.clinicId)}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t pt-2">
            <span className="font-medium">Total</span>
            <span className="tabular-nums font-medium">
              {formatClinicPrice(group.total, invoiceClinicId)}
            </span>
          </div>

          <Button
            variant="outline"
            className="w-full"
            disabled={isPending}
            onClick={() => openInvoice(group.paymentId)}
          >
            {isPending ? (
              <Loader2 className="size-4 mr-1 animate-spin" />
            ) : (
              <ExternalLink className="size-4 mr-1" />
            )}
            View Invoice
          </Button>
        </div>
      )}
    </div>
  )
}
